import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { API } from 'app/api';
import { getSupportedLang, LanguageStore, type SupportedLang, useTranslation } from 'app/lang';
import { LANG_TITLE_BY_CODE } from 'app/lang/constants';
import { useLanguageSender } from 'app/lib/signalr';
import { cookie } from 'app/packages/lib';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';
import { SELECTED_LANGUAGE } from 'app/ssr/config';
import Dropdown, { ChangeHandler } from 'app/widgets/Dropdown/Single';

import DropdownComponents from './DropdownComponents';

import styles from './LanguageSelector.module.scss';

type Countries = Country[];

export type Country = {
  label: string;
  value: SupportedLang;
};

const countries: Countries = (Object.keys(LANG_TITLE_BY_CODE) as SupportedLang[])
  .map((keys) => ({
    label: LANG_TITLE_BY_CODE[keys as keyof typeof LANG_TITLE_BY_CODE],
    value: keys,
  }));

const LanguageSelector = () => {
  const { lang } = useTranslation();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  useLanguageSender();

  const value = useMemo(() => (
    { label: LANG_TITLE_BY_CODE[lang], value: lang }
  ), [lang]);

  const changeUserLanguage = async (language: SupportedLang) => {
    if (isLoggedIn) {
      await API.client.clientProfile.changeLanguage({ language });
    }

    cookie.setCookie(SELECTED_LANGUAGE, language);
  };

  const handleChange: ChangeHandler<Country> = useCallback(async (option) => {
    if (option) {
      await changeUserLanguage(option.value);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const syncLanguageBetweenTabs = (() => {
      let lastLang = LanguageStore.getLang();

      return async () => {
        const currentLang = getSupportedLang(cookie.getCookieByKey(SELECTED_LANGUAGE));

        if (lastLang != currentLang) {
          lastLang = currentLang;

          await LanguageStore.changeLanguage(currentLang);
          await changeUserLanguage(currentLang);
        }
      };
    })();

    const intervalId = setInterval(syncLanguageBetweenTabs, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <Dropdown components={DropdownComponents()}
              className={styles.languageDropdown}
              value={value}
              onChange={handleChange}
              options={countries} />
  );
};

export default LanguageSelector;
